<template>
  <section id="games" class="py-16 lg:py-24 bg-light-gray">
    <div class="text-center mb-16 reveal">
      <div class="accent-line mx-auto w-24 mb-4" />
      <h2 class="text-3xl md:text-4xl font-display font-bold mb-4 text-dark-text">
        <span class="text-gradient bg-gradient-to-r from-primary to-secondary">游戏产品</span>
      </h2>
      <p class="text-xl text-gray-300 max-w-2xl mx-auto font-bold">OUR GAMES</p>
    </div>

    <!-- 主要内容区 -->
    <div class="container mx-auto px-4 md:py-14 flex flex-col md:flex-row gap-8 md:gap-16">

      <!-- 左侧大图区域 - 包含主视觉图和外部的角色头像栏 -->
      <div
        class="small_screen_img w-full md:w-2/3 relative"
        :class="{ 'left-animate': isAnimated }"
      >
        <!-- 主视觉图 - 固定宽高 -->
        <div
          id="img"
          :key="currentImageKey"
          class="relative rounded-lg overflow-hidden shadow-2xl hover-scale w-full h-[20rem] lg:h-[37.5rem]"
        >
          <transition name="fade" mode="out-in">
            <img
              v-if="currentImageUrl"
              key="currentImage"
              :src="imgPath + currentImageUrl"
              class="w-full h-full object-cover"
              alt=""
            >
            <div v-else class="coming-soon">
              敬请期待
            </div>
          </transition>
        </div>

        <!-- 角色头像栏 - 居右，尺寸放大 -->
        <div
          id="character"
          :key="currentCharacterKey"
          class="mt-4 ml-auto flex gap-3 p-2 w-fit absolute right-0"
        >
          <div
            v-for="(char, index) in games_data"
            :key="char.id"
            class="w-14 h-14 rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
            :class="currentCharacter.id === char.id ? 'border-4 border-secondary scale-110' : 'border-4 border-transparent hover:border-secondary/70'"
            @click="switchCharacter(index)"
          >
            <img
              v-if="index < games_data.length - 1"
              :src="imgPath + char.a"
              :alt="char.n"
              class="w-full h-full object-cover"
            >
            <!-- 紫白色问号头像 -->
            <div v-else class="w-full h-full object-cover bg-white avatar-gradient flex items-center justify-center shadow-lg">
              <i class="text-primary drop-shadow-md text-sm">敬请<br>期待</i>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧信息区域 - 文字居右 -->
      <div
        ref="infoSection"
        class="small_screen_content w-full md:w-1/3 flex flex-col justify-between"
        :class="{ 'right-animate': isAnimated }"
      >
        <!-- 标题区域 -->
        <div>
          <h1 v-if="currentGame.title" class="text-3xl md:text-5xl lg:text-6xl font-bold font-display mb-2 text-shadow text-black text-right">
            {{ currentGame.title }}
          </h1>
          <p v-if="currentGame.enTitle" class="text-lg mb-8 text-gray-300 text-right font-bold">{{ currentGame.enTitle }}</p>

          <p v-if="currentGame.content" class="mb-8 text-gray-500 text-left">{{ currentGame.content }}</p>

          <!-- 社交媒体链接 -->
          <div class="flex flex-wrap justify-end gap-4 md:mb-8 game-social-icon">
            <div v-if="currentGame.link" class="hover:bg-light-gray">
              <a :href="currentGame.link">
                <img src="@/icons/svg/home.svg" alt="首页">
              </a>
            </div>
            <div v-if="currentGame.weibo">
              <a :href="currentGame.weibo">
                <img src="@/icons/svg/weibo.svg" alt="微博">
              </a>
            </div>
            <div v-if="currentGame.wechat">
              <a alt="微信公众号" class="wechat-container">
                <img id="weChatBtn" src="@/icons/svg/wechat.svg" alt="微信公众号" @click="clickWechat">
                <!-- 二维码图片（默认隐藏） -->
                <img id="weChatQR" :src="currentGame.wechat" class="wechat-qrcode" alt="公众号二维码">
              </a>
            </div>
            <div v-if="currentGame.bili">
              <a :href="currentGame.bili">
                <img src="@/icons/svg/bili.svg" alt="哔哩哔哩">
              </a>
            </div>
            <div v-if="currentGame.taptap">
              <a :href="currentGame.taptap">
                <img src="@/icons/svg/taptap.svg" alt="taptap">
              </a>
            </div>
            <div v-if="currentGame.facebook">
              <a :href="currentGame.facebook">
                <img src="@/icons/svg/facebook.svg" alt="facebook">
              </a>
            </div>
            <div v-if="currentGame.discord">
              <a :href="currentGame.discord">
                <img src="@/icons/svg/discord.svg" alt="discord">
              </a>
            </div>
            <div v-if="currentGame.twitter">
              <a :href="currentGame.twitter">
                <img src="@/icons/svg/twitter.svg" alt="twitter">
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import vv from '@/components/varVariable.vue'
import { select_games } from '@/api/games'

export default {
  name: 'Game',
  data: function() {
    return {
      // 所有游戏
      games_data: [],
      currentCharacter: {
        id: '',
        name: '',
        avatarUrl: undefined,
        imageUrl: undefined
      },
      currentGame: {
        title: '',
        enTitle: '',
        content: '',
        weibo: '',
        wechat: '',
        bili: '',
        taptap: '',
        link: '',
        facebook: '',
        discord: '',
        twitter: ''
      },
      currentImageUrl: '',
      isAnimated: false,
      currentImageKey: 0, // 用于触发img元素的重新渲染
      currentCharacterKey: 0, // 用于触发character元素的重新渲染
      autoSwitchTimer: null, // 自动切换定时器
      currentIndex: 0 // 当前游戏索引，方便自动切换
    }
  },
  computed: {
    imgPath() {
      return vv.fileHttp
    }
  },
  created() {
    // 获取所有游戏（保留异步操作）
    this.loadGamesData()
  },
  mounted: function() {
    // 这里只放不依赖 games_data 的逻辑
    // 例如初始动画（如果不需要依赖数据的话）
    // this.startAnimation()

    // 滚动监听可以保留，但触发时的逻辑确保数据已加载
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.games_data.length > 0) {
            this.startAnimation()
          }
        })
      },
      { threshold: 0.1 }
    )

    observer.observe(this.$refs.infoSection)

    // 监听全局点击
    document.addEventListener('click', (event) => {
      // 判断点击目标是否是按钮/二维码本身，或它们的子元素
      const divId = event.target.id

      // 若点击其他区域，移除显示类
      if (divId !== 'weChatQR' && divId !== 'weChatBtn') {
        document.getElementById('weChatQR').classList.remove('always-show')
      }
    })
  },
  // 组件销毁前清除定时器
  beforeDestroy() {
    this.clearAutoSwitchTimer()
  },
  methods: {
    // 提取加载数据的方法
    async loadGamesData() {
      await select_games()
        .then(data => {
          this.games_data.splice(0)
          const dl = data.d.length
          for (let i = 0; i < dl; i++) {
            if (data.d[i].c.zh.n) {
              const item = {
                id: i + 1,
                n: data.d[i].n,
                a: data.d[i].c.zh.a,
                c: data.d[i].c.zh.c,
                e: data.d[i].c.zh.e,
                i: data.d[i].c.zh.i,
                link: data.d[i].c.common.link,
                weibo: data.d[i].c.common.weibo,
                wechat: data.d[i].c.common.wechat,
                bili: data.d[i].c.common.bili,
                taptap: data.d[i].c.common.taptap,
                facebook: data.d[i].c.common.facebook,
                discord: data.d[i].c.common.discord,
                twitter: data.d[i].c.common.twitter,
                addTime: data.d[i].d
              }
              this.games_data.push(item) // 简化数组添加方式
            }
          }

          // 根据游戏添加时间排序
          this.games_data.sort((a, b) => {
            // 处理时间格式：如果是字符串时间（如'2025-11-06'），转换为时间戳比较
            const timeA = new Date(a.addTime).getTime()
            const timeB = new Date(b.addTime).getTime()
            // 降序排序（后发生的时间在前）
            return timeB - timeA
          })

          this.games_data.push({
            id: dl + 1,
            n: '敬请期待',
            a: undefined,
            c: undefined,
            e: undefined,
            i: undefined,
            link: undefined,
            weibo: undefined,
            wechat: undefined,
            bili: undefined,
            taptap: undefined,
            facebook: undefined,
            discord: undefined,
            twitter: undefined
          })

          // 数据加载完成后，执行依赖数据的初始化逻辑
          this.initCharacterAndGame()
          // 数据加载完成后立即启动自动切换
          this.startAutoSwitch()
        })
        .catch(error => {
          console.error('加载游戏数据失败:', error)
          // 处理请求失败的情况，避免后续逻辑报错
        })
    },
    // 提取依赖 games_data 的初始化逻辑
    initCharacterAndGame() {
      if (this.games_data.length === 0) {
        console.warn('没有游戏数据可供初始化')
        return
      }
      // 初始化角色和游戏信息
      this.currentCharacter = {
        id: this.games_data[0].id,
        name: this.games_data[0].n,
        avatarUrl: this.games_data[0].a,
        imageUrl: this.games_data[0].i
      }
      this.currentGame = {
        title: this.games_data[0].n,
        enTitle: this.games_data[0].e,
        content: this.games_data[0].c,
        facebook: this.games_data[0].facebook,
        discord: this.games_data[0].discord,
        twitter: this.games_data[0].twitter,
        link: this.games_data[0].link || undefined,
        weibo: this.games_data[0].weibo,
        wechat: this.games_data[0].wechat,
        bili: this.games_data[0].bili,
        taptap: this.games_data[0].taptap
      }
      this.currentImageUrl = this.currentCharacter.imageUrl

      // 数据加载完成后再执行动画
      this.startAnimation()
    },
    // 手动切换角色
    switchCharacter(index) {
      // 点击切换时停止自动切换定时器
      this.clearAutoSwitchTimer()
      this.autoSwitchCharacter(index)
    },
    // 切换角色
    autoSwitchCharacter(index) {
      if (index < this.games_data.length - 1) {
        this.currentCharacter = {
          id: this.games_data[index].id,
          name: this.games_data[index].n,
          avatarUrl: this.games_data[index].a,
          imageUrl: this.games_data[index].i
        }
        this.currentGame = {
          title: this.games_data[index].n,
          enTitle: this.games_data[index].e,
          content: this.games_data[index].c,
          facebook: this.games_data[index].facebook,
          discord: this.games_data[index].discord,
          twitter: this.games_data[index].twitter,
          link: this.games_data[index].link || undefined,
          weibo: this.games_data[index].weibo,
          wechat: this.games_data[index].wechat,
          bili: this.games_data[index].bili,
          taptap: this.games_data[index].taptap
        }
        this.currentImageUrl = this.currentCharacter.imageUrl
        // 更新key值以触发重新渲染
        this.currentImageKey += 1
        this.currentCharacterKey += 1
        this.startAnimation()
      }
      this.currentIndex = index
    },

    // 开始动画
    startAnimation() {
      // 重置动画状态
      this.isAnimated = false
      // 触发重绘后再启用动画
      setTimeout(() => {
        this.$nextTick(() => {
          this.isAnimated = true
        }, 50)
      })
    },

    // 开始自动切换
    startAutoSwitch() {
      // 清除已有的定时器
      this.clearAutoSwitchTimer()

      // 如果只有一个游戏（不包含最后那个"敬请期待"），则不自动切换
      const validGamesCount = this.games_data.length - 1
      if (validGamesCount <= 1) {
        return
      }

      // 设置新的定时器，每3秒切换一次
      this.autoSwitchTimer = setInterval(() => {
        this.autoSwitchToNext()
      }, 4000)
    },

    // 新增：清除自动切换定时器
    clearAutoSwitchTimer() {
      if (this.autoSwitchTimer) {
        clearInterval(this.autoSwitchTimer)
        this.autoSwitchTimer = null
      }
    },

    // 新增：自动切换到下一个游戏
    autoSwitchToNext() {
      const validGamesCount = this.games_data.length - 2
      let nextIndex = this.currentIndex + 1
      if ((this.currentIndex >= validGamesCount)) {
        nextIndex = 0
      }

      // 切换到下一个游戏
      this.autoSwitchCharacter(nextIndex)
    },

    // 点击微信图标让二维码永久显示
    clickWechat() {
      document.getElementById('weChatQR').classList.add('always-show')
    }
  }
}
</script>

<style scoped>
.coming-soon {
  display: flex;
  justify-content: center; /* 水平居中 */
  align-items: center;     /* 垂直居中 */
  font-size: 2.5rem;
  color: #7b2cbf;
  background-color: #ffffff;
  height: 100%;
  width: 100%;
}

.game-social-icon {
  a {
    align-self: center;
    margin-right: 3rem;
    img {
      width: 2rem;
      height: 2rem;
    }
    img:not(#weChatBtn) {
      pointer-events: none;
    }

    &:hover img:not(#weChatQR) {
      transform: scale(1.04);
      filter: brightness(0) saturate(100%) invert(23%) sepia(58%) saturate(3502%) hue-rotate(260deg) brightness(95%) contrast(95%);
    }
  }
}

/* 移动端适配（屏幕宽度 ≤ 800px 时） */
@media (max-width: 800px) {
  .container {
    align-items: center;
    flex-direction: column;
    gap: 15px;
  }

  #character > div {
    width: 3rem;
    height: 3rem;
  }

  /* 确保问号在移动端容器中居中 */
  #character .avatar-gradient i {
    font-size: 1rem; /* 调整问号大小以适应5rem的容器 */
    line-height: 1; /* 重置行高 */
    margin: 0; /* 移除可能的外边距 */
  }

  .small_screen_img {
    margin-bottom: 2rem;
  }

  .small_screen_content {
    text-align: center;
    padding-top: 4rem;
    div {
      div {
        justify-content: center;
        margin-top: 1rem;
      }
      p:nth-child(3) {
        height: 15rem;
        line-height: 1.8rem;
        font-size: 1rem;
      }
    }
  }
}

/* 左侧区域动画 - 从左由浅变深进入 */
.left-animate {
  animation: leftEnter 1s ease-out forwards;
  opacity: 0;
  transform: translateX(-100px);
}

@keyframes leftEnter {
  0% {
    opacity: 0;
    transform: translateX(-100px);
    filter: brightness(1.2) contrast(0.8);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
    filter: brightness(1) contrast(1);
  }
}

/* 子元素动画 - 移除对父元素类的依赖 */
#img {
  animation: leftEnter 1s ease-out 0.1s forwards;
  opacity: 0;
  transform: translateX(-100px);
}

#character {
  animation: leftEnter 1s ease-out 0.3s forwards;
  opacity: 0;
  transform: translateX(-100px);
}

/* 右侧区域动画 - 从右由浅变深进入 */
.right-animate {
  animation: rightEnter 1s ease-out forwards;
  opacity: 0;
  transform: translateX(30px);
}

@keyframes rightEnter {
  0% {
    opacity: 0;
    transform: translateX(30px);
    color: rgba(0, 0, 0, 0.3);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
    color: rgba(0, 0, 0, 1);
  }
}

/* 右侧区域子元素延迟动画 */
.right-animate h1.text-black {
  animation: rightEnter 1s ease-out 0.1s forwards;
  opacity: 0;
  transform: translateX(30px);
}

.right-animate p.text-black {
  animation: rightEnter 1s ease-out 0.1s forwards;
  opacity: 0;
  transform: translateX(30px);
}

.right-animate p.text-black:nth-child(2) {
  animation: rightEnter 1s ease-out 0.2s forwards;
  opacity: 0;
  transform: translateX(30px);
}

.right-animate p.text-black:nth-child(3) {
  animation: rightEnter 1s ease-out 0.4s forwards;
  opacity: 0;
  transform: translateX(30px);
}

.right-animate div.flex {
  animation: rightEnter 1s ease-out 0.6s forwards;
  opacity: 0;
  transform: translateX(30px);
}

.right-animate > div:last-child {
  animation: rightEnter 1s ease-out 0.7s forwards;
  opacity: 0;
  transform: translateX(30px);
}

.text-shadow {
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.hover-scale {
  transition: transform 0.3s ease;
}

.hover-scale:hover {
  transform: scale(1.03);
}

/* 容器：相对定位，作为二维码的定位参考 */
.wechat-container {
  cursor: pointer; /* 鼠标放上去显示手型 */
}

/* 二维码样式：默认隐藏，绝对定位 */
.wechat-qrcode {
  /* 小尺寸设置（比图二的官方社区二维码小） */
  width: 7rem !important; /* 二维码宽度，可按需调整 */
  height: 7rem !important; /* 保持比例 */
  object-fit: contain;

  /* 定位：放在图标正下方（距离图标5px） */
  position: absolute;
  top: calc(100%); /* 图标底部+5px */
  transform: translateX(-30%); /* 水平居中对齐图标 */

  /* 默认隐藏 */
  opacity: 0;
  visibility: hidden;
  pointer-events: none; /* 隐藏时不响应鼠标事件 */

  /* 显示时的过渡动画（平滑出现） */
  transition: all 0.3s ease;

  /* 避免被其他元素遮挡 */
  /* 可选：添加边框和阴影更美观 */
  padding: 5px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);

  z-index: 100 !important; /* 提高层级，确保在最上层接收触摸事件 */
}

/* 鼠标悬停在容器上时，显示二维码 */
.wechat-container:hover .wechat-qrcode {
  opacity: 1;
  visibility: visible;
}

.always-show {
  @media screen and (max-width: 800px) {
    visibility: visible !important;
    opacity: 1 !important;
    pointer-events: all !important;
  }
}
</style>
